import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import type { PaginationState } from '@tanstack/vue-table'
import type {
  QueryParamsByPath,
  ResponseByPath,
  SchemaAttributes,
  Schemas,
} from '@/api/types.js'
import type { NormalizedClient } from '@/models/client.js'
import { buildClientRelationships } from '@/composables/clients.js'
import {
  buildPatientHealthPlanItemRelationships,
  type NormalizedPatientHealthPlanItem,
} from '@/composables/patientHealthPlanItems.js'
import {
  buildPatientRelationships,
  type NormalizedPatient,
} from '@/composables/patients.js'
import { useApi } from '@/composables/useApi.js'
import useTranslation from '@/composables/useTranslation.js'
import {
  buildIncludedResourceMap,
  createRelationshipBuilder,
  one,
  toPaginationQueryParams,
  type IncludedResourceMap,
  type RelationshipDefs,
} from '@/utils/jsonapi.js'
const PATIENT_HEALTH_PLANS_LIST_URL = '/{provet_id}/api/patient_health_plans'
export type PatientHealthPlanListResponse = ResponseByPath<
  'get',
  typeof PATIENT_HEALTH_PLANS_LIST_URL
>
export type PatientHealthPlanListQueryParams = QueryParamsByPath<
  'get',
  typeof PATIENT_HEALTH_PLANS_LIST_URL
>

const PATIENT_HEALTH_PLANS_DETAIL_URL =
  '/{provet_id}/api/patient_health_plans/{id}'
export type PatientHealthPlanDetailResponse = ResponseByPath<
  'get',
  typeof PATIENT_HEALTH_PLANS_DETAIL_URL
>
export type PatientHealthPlanDetailQueryParams = QueryParamsByPath<
  'get',
  typeof PATIENT_HEALTH_PLANS_DETAIL_URL
>

export type PatientHealthPlanAttributes = SchemaAttributes<'PatientHealthPlan'>

type PatientHealthPlanIncludedResourceMap = IncludedResourceMap<
  | 'Client'
  | 'Patient'
  | 'Phone'
  | 'PatientHealthPlanItem'
  | 'Consultation'
  | 'OrganizationItem'
>

export type PatientHealthPlanStatus = NonNullable<
  PatientHealthPlanAttributes['status']
>

export const PatientHealthPlanStatuses: Record<
  string,
  PatientHealthPlanStatus
> = {
  Active: 'ACTIVE',
  AppliedFor: 'APPLIED_FOR',
  Signed: 'SIGNED',
  Suspended: 'SUSPENDED',
  Lapsed: 'LAPSED',
  Ended: 'ENDED',
}

export type PatientHealthPlanPaymentMethod = NonNullable<
  PatientHealthPlanAttributes['payment_method']
>

export const PatientHealthPlanPaymentMethods: Record<
  string,
  PatientHealthPlanPaymentMethod
> = {
  NotSpecified: 'NOT_SPECIFIED',
  ProvetBilling: 'PROVET_BILLING', // Monthly billing through Provet - value = 1
  ProvetPrepayment: 'PROVET_PREPAYMENT', // Contract period as advance payment - value = 2

  // Commented out the options below because they are not used in the current implementation
  // ProvetSubscription: 'PROVET_SUBSCRIPTION', // Monthly recurring payments through Nordhealth Pay - value = 3
  // GravityPayment: 'GRAVITY_PAYMENT', // Monthly recurring payments through Gravity Payment - value = 4
  // ExternalPayment: 'EXTERNAL_PAYMENT', // External payment - value = 5
}

export type PatientHealthPlanCancelReason = NonNullable<
  PatientHealthPlanAttributes['cancel_reason']
>

export const PatientHealthPlanCancelReasons: Record<
  string,
  PatientHealthPlanCancelReason
> = {
  Death: 'DEATH',
  MissingPayments: 'MISSING_PAYMENTS',
  Other: 'OTHER',
}

export type NormalizedPatientHealthPlan = {
  id: string
  attributes?: PatientHealthPlanAttributes
  // relationships
  client?: NormalizedClient
  patient?: NormalizedPatient
  lastUsedItem?: NormalizedPatientHealthPlanItem
  lastConsultation?: Schemas['Consultation']
}

export type NormalizedPatientHealthPlanListResponse = Omit<
  PatientHealthPlanListResponse,
  'data'
> & {
  data: NormalizedPatientHealthPlan[]
}

const patientHealthPlansKeys = {
  all: ['healthPlans'] as const,
  lists: () => [...patientHealthPlansKeys.all, 'list'] as const,
  list: (query: MaybeRef<PatientHealthPlanListQueryParams>) =>
    [...patientHealthPlansKeys.lists(), query] as const,
  detail: (id: MaybeRef<string>) =>
    [...patientHealthPlansKeys.all, 'detail', id] as const,
}

export function usePatientHealthPlanDetailQuery(
  healthPlanId: MaybeRef<string>,
) {
  const api = useApi()
  const route = useRoute()

  const query = useQuery({
    queryKey: patientHealthPlansKeys.detail(healthPlanId),
    queryFn: () => {
      return api((client) =>
        client.GET(PATIENT_HEALTH_PLANS_DETAIL_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              id: unref(healthPlanId),
            },
          },
        }),
      )
    },
  })

  return reactive(query)
}

/**
 * Normalize a patient health plan response to a more usable format.
 * @param patientHealthPlan - The patienthealth plan response to normalize.
 * @returns The normalized patient health plan.
 */
function normalizePatientHealthPlan(
  patientHealthPlan: Schemas['PatientHealthPlan'],
  included?: PatientHealthPlanIncludedResourceMap,
): NormalizedPatientHealthPlan {
  const normalizedItem: NormalizedPatientHealthPlan = {
    id: patientHealthPlan.id,
    attributes: patientHealthPlan.attributes,
  }

  if (included) {
    const relationships = buildPatientHealthPlanRelationships(
      patientHealthPlan,
      included,
    )

    normalizedItem.client = relationships.client
    normalizedItem.patient = relationships.patient
    normalizedItem.lastConsultation = relationships.last_consultation
    const lastUsedItem = relationships.last_used_item

    if (normalizedItem.client?.id) {
      const relationships = buildClientRelationships(
        normalizedItem.client,
        included,
      )
      normalizedItem.client.phoneNumbers = relationships.phone_numbers
    }
    if (normalizedItem.patient?.id) {
      const relationships = buildPatientRelationships(
        normalizedItem.patient,
        included,
      )
      normalizedItem.patient.activeHealthPlans =
        relationships.active_health_plans
    }
    if (lastUsedItem?.id) {
      const relationships = buildPatientHealthPlanItemRelationships(
        lastUsedItem,
        included,
      )
      normalizedItem.lastUsedItem = {
        id: lastUsedItem.id,
        attributes: lastUsedItem.attributes,
        organizationItem: relationships.item,
      }
    }
  }

  return normalizedItem
}

type PatientHealthPlanListFilters = {
  search?: string
  department?: string
  status?: PatientHealthPlanStatus
  paymentMethod?: PatientHealthPlanPaymentMethod
}

type PatientHealthPlanListQueryParameters = {
  pagination: Ref<PaginationState>
  filters: MaybeRef<PatientHealthPlanListFilters>
  includes?: MaybeRef<string[]>
}

export function usePatientHealthPlanListQuery({
  pagination,
  filters = {},
  includes = [
    'client',
    'client.phone_numbers',
    'patient',
    'patient.active_health_plans',
    'last_used_item',
    'last_used_item.item',
    'last_consultation',
  ],
}: PatientHealthPlanListQueryParameters) {
  const api = useApi()
  const route = useRoute()

  const queryParams = computed<PatientHealthPlanListQueryParams>(() => {
    const { search, status, paymentMethod, department } = unref(filters)

    return {
      ...toPaginationQueryParams(pagination),
      'filter[search]': search || undefined,
      'filter[department.id]': department || undefined,
      'filter[status]': status || undefined,
      'filter[payment_method]': paymentMethod || undefined,
      include: unref(includes),
    } satisfies PatientHealthPlanListQueryParams
  })

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: patientHealthPlansKeys.list(queryParams),
    queryFn: async () => {
      const response = await api((client) =>
        client.GET(PATIENT_HEALTH_PLANS_LIST_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queryParams.value,
          },
        }),
      )

      return response
    },
    select: (response): NormalizedPatientHealthPlanListResponse => {
      const included = buildIncludedResourceMap(response, [
        'Client',
        'Phone',
        'Patient',
        'PatientHealthPlan',
        'PatientHealthPlanItem',
        'Consultation',
        'OrganizationItem',
      ])

      return {
        meta: response.meta,
        data: response.data.map((item) => {
          return normalizePatientHealthPlan(item, included)
        }),
        // nuked the `included` since the data is already normalized
      }
    },
  })

  return reactive(query)
}

const patientHealthPlanRelationships = {
  client: one('Client'),
  patient: one('Patient'),
  last_used_item: one('PatientHealthPlanItem'),
  last_consultation: one('Consultation'),
} satisfies RelationshipDefs<Schemas['PatientHealthPlan']>

export const buildPatientHealthPlanRelationships = createRelationshipBuilder(
  patientHealthPlanRelationships,
)

export function useEnumPatientHealthPlanStatusTranslation() {
  const translate = (
    patientHealthPlanStatus?: MaybeRef<PatientHealthPlanStatus>,
  ) => {
    if (!patientHealthPlanStatus) {
      return
    }

    const tEnumPatientHealthPlanStatus = useTranslation(
      'common.enums.patientHealthPlanStatus',
    )
    const key = unref(patientHealthPlanStatus)

    return {
      [PatientHealthPlanStatuses.Active]:
        tEnumPatientHealthPlanStatus('active'),
      [PatientHealthPlanStatuses.Ended]: tEnumPatientHealthPlanStatus('ended'),
      [PatientHealthPlanStatuses.Lapsed]:
        tEnumPatientHealthPlanStatus('cancelled'),
      [PatientHealthPlanStatuses.Suspended]:
        tEnumPatientHealthPlanStatus('suspended'),
      [PatientHealthPlanStatuses.AppliedFor]:
        tEnumPatientHealthPlanStatus('appliedFor'),
      [PatientHealthPlanStatuses.Signed]:
        tEnumPatientHealthPlanStatus('signed'),
    }[key]
  }

  return { translate }
}

export function useEnumPatientHealthPlanPaymentMethodTranslation() {
  const translate = (
    patientHealthPlanPaymentMethod?: MaybeRef<PatientHealthPlanPaymentMethod>,
  ) => {
    if (!patientHealthPlanPaymentMethod) {
      return
    }

    const tEnumPatientHealthPlanPaymentMethod = useTranslation(
      'common.enums.patientHealthPlanPaymentMethod',
    )
    const key = unref(patientHealthPlanPaymentMethod)

    return {
      [PatientHealthPlanPaymentMethods.NotSpecified]:
        tEnumPatientHealthPlanPaymentMethod('notSpecified'),
      [PatientHealthPlanPaymentMethods.ProvetBilling]:
        tEnumPatientHealthPlanPaymentMethod('provetBilling'),
      [PatientHealthPlanPaymentMethods.ProvetPrepayment]:
        tEnumPatientHealthPlanPaymentMethod('provetPrepayment'),
    }[key]
  }

  return { translate }
}

export function useEnumPatientHealthPlanCancelReasonTranslation() {
  const translate = (
    patientHealthPlanCancelReason?: MaybeRef<PatientHealthPlanCancelReason>,
  ) => {
    if (!patientHealthPlanCancelReason) {
      return
    }

    const tEnumPatientHealthPlanCancelReason = useTranslation(
      'common.enums.patientHealthPlanCancelReason',
    )
    const key = unref(patientHealthPlanCancelReason)

    return {
      [PatientHealthPlanCancelReasons.Death]:
        tEnumPatientHealthPlanCancelReason('death'),
      [PatientHealthPlanCancelReasons.MissingPayments]:
        tEnumPatientHealthPlanCancelReason('missingPayments'),
      [PatientHealthPlanCancelReasons.Other]:
        tEnumPatientHealthPlanCancelReason('other'),
    }[key]
  }

  return { translate }
}
